import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  History,
  KeyRound,
  Plus,
  RefreshCw,
  RotateCw,
  Trash2,
  Webhook,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canEditOwn, useAuthStore } from "@/features/auth/store";
import {
  webhooksApi,
  type Delivery,
  type EventType,
  type WebhookEndpoint,
} from "@/features/webhooks/api";
import { cn } from "@/lib/utils";

const KEY = ["webhooks"] as const;

const DELIVERY_STATUS_CLS: Record<string, string> = {
  sent: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  failed: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
  dlq: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
  pending: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
};

export default function WebhooksPage() {
  const role = useAuthStore((s) => s.user?.role ?? "member");
  // Customers own everything they can see here, so rank does not apply.
  const canEdit = canEditOwn(role);
  const qc = useQueryClient();

  const endpointsQ = useQuery({
    queryKey: [...KEY, "endpoints"],
    queryFn: webhooksApi.listEndpoints,
    staleTime: 15_000,
  });
  const eventTypesQ = useQuery({
    queryKey: [...KEY, "event-types"],
    queryFn: webhooksApi.eventTypes,
    staleTime: 5 * 60_000,
  });

  const endpoints = endpointsQ.data ?? [];
  const eventTypes = eventTypesQ.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpoint | null>(null);
  const [historyTarget, setHistoryTarget] = useState<WebhookEndpoint | null>(
    null,
  );
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: [...KEY, "endpoints"] });
  }

  const deleteM = useMutation({
    mutationFn: (id: string) => webhooksApi.deleteEndpoint(id),
    onSuccess: invalidate,
  });

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteM.mutateAsync(deleteTarget.id);
      toast.success("Endpoint deleted");
    } catch {
      toast.error("Failed to delete endpoint");
    }
    setDeleteTarget(null);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Webhooks</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Receive real-time event notifications at your own HTTPS endpoints.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Add Endpoint
          </Button>
        )}
      </div>

      {endpointsQ.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {!endpointsQ.isLoading && endpoints.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <Webhook className="size-10 text-muted-foreground" />
            <p className="text-base font-medium">No webhook endpoints yet</p>
            <p className="text-sm text-muted-foreground">
              Click "Add Endpoint" to receive event notifications.
            </p>
          </CardContent>
        </Card>
      )}

      {!endpointsQ.isLoading &&
        endpoints.map((ep) => (
          <EndpointAccordion
            key={ep.id}
            endpoint={ep}
            eventTypes={eventTypes}
            canEdit={canEdit}
            onChange={invalidate}
            onDelete={() => setDeleteTarget(ep)}
            onHistory={() => setHistoryTarget(ep)}
            onRotated={setRotatedSecret}
          />
        ))}

      {createOpen && (
        <CreateEndpointDialog
          eventTypes={eventTypes}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={invalidate}
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete endpoint</AlertDialogTitle>
            <AlertDialogDescription>
              Delete this webhook endpoint? All delivery history will be
              removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteM.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleteM.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={!!rotatedSecret}
        onOpenChange={(v) => !v && setRotatedSecret(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Signing Secret</DialogTitle>
            <DialogDescription>
              Copy this secret now — it will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <pre className="break-all rounded-md bg-muted p-3 font-mono text-xs">
            {rotatedSecret}
          </pre>
          <DialogFooter>
            <Button
              onClick={() => {
                if (rotatedSecret) {
                  navigator.clipboard.writeText(rotatedSecret);
                  toast.success("Copied to clipboard");
                }
              }}
            >
              Copy
            </Button>
            <Button variant="outline" onClick={() => setRotatedSecret(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {historyTarget && (
        <DeliveryHistoryDialog
          endpoint={historyTarget}
          onClose={() => setHistoryTarget(null)}
        />
      )}
    </div>
  );
}

function EndpointAccordion({
  endpoint: ep,
  eventTypes,
  canEdit,
  onChange,
  onDelete,
  onHistory,
  onRotated,
}: {
  endpoint: WebhookEndpoint;
  eventTypes: EventType[];
  canEdit: boolean;
  onChange: () => void;
  onDelete: () => void;
  onHistory: () => void;
  onRotated: (secret: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const subscribed = ep.subscriptions
    .filter((s) => s.enabled)
    .map((s) => s.event_type);

  const toggleEndpoint = useMutation({
    mutationFn: () =>
      webhooksApi.updateEndpoint(ep.id, { enabled: !ep.enabled }),
    onSuccess: onChange,
    onError: () => toast.error("Failed to update endpoint"),
  });

  const toggleSub = useMutation({
    mutationFn: async ({ evt, on }: { evt: string; on: boolean }) => {
      if (on) await webhooksApi.addSubscriptions(ep.id, [evt]);
      else await webhooksApi.removeSubscription(ep.id, evt);
    },
    onSuccess: onChange,
    onError: () => toast.error("Failed to update subscription"),
  });

  const rotate = useMutation({
    mutationFn: () => webhooksApi.rotateSecret(ep.id),
    onSuccess: (data) => {
      onChange();
      onRotated(data.secret);
    },
    onError: () => toast.error("Failed to rotate secret"),
  });

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
        <Webhook
          className={cn(
            "size-5 shrink-0",
            ep.enabled ? "text-indigo-500" : "text-muted-foreground",
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{ep.url}</p>
          {ep.description && (
            <p className="truncate text-xs text-muted-foreground">
              {ep.description}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
              ep.enabled
                ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
                : "text-muted-foreground bg-muted",
            )}
          >
            {ep.enabled ? "Active" : "Disabled"}
          </span>
          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs">
            {subscribed.length} events
          </span>
          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs">
            {ep.total_deliveries} sent
          </span>
          {ep.failed_deliveries > 0 && (
            <span className="inline-flex items-center rounded-full border border-rose-300 px-2 py-0.5 text-xs text-rose-600 dark:border-rose-900 dark:text-rose-400">
              {ep.failed_deliveries} failed
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t px-4 py-4 grid gap-6 md:grid-cols-2">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={ep.enabled}
                onCheckedChange={() => toggleEndpoint.mutate()}
                disabled={!canEdit || toggleEndpoint.isPending}
                aria-label="Endpoint enabled"
              />
              <span className="text-sm">Endpoint enabled</span>
            </div>

            <div>
              <p className="mb-1.5 text-sm font-medium">Subscribed Events</p>
              <div className="flex flex-col gap-1.5">
                {eventTypes.map((et) => {
                  const on = subscribed.includes(et.value);
                  return (
                    <label
                      key={et.value}
                      className="flex cursor-pointer items-start gap-2"
                    >
                      <Checkbox
                        checked={on}
                        onCheckedChange={() =>
                          toggleSub.mutate({ evt: et.value, on: !on })
                        }
                        disabled={!canEdit || toggleSub.isPending}
                        className="mt-0.5"
                      />
                      <span className="text-sm">
                        <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                          {et.value}
                        </code>
                        <span className="ml-1.5 text-muted-foreground">
                          — {et.label}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Actions</p>
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={onHistory}>
                <History className="size-4" />
                View Delivery History
              </Button>
              {canEdit && (
                <Button
                  variant="outline"
                  onClick={() => rotate.mutate()}
                  disabled={rotate.isPending}
                >
                  <KeyRound className="size-4" />
                  {rotate.isPending
                    ? "Rotating…"
                    : ep.has_secret
                    ? "Rotate Secret"
                    : "Set Signing Secret"}
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={onDelete}
                >
                  <Trash2 className="size-4" />
                  Delete Endpoint
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function CreateEndpointDialog({
  eventTypes,
  open,
  onOpenChange,
  onCreated,
}: {
  eventTypes: EventType[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [secret, setSecret] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const create = useMutation({
    mutationFn: () =>
      webhooksApi.createEndpoint({
        url: url.trim(),
        description: description.trim() || undefined,
        secret: secret.trim() || undefined,
        event_types: selected,
      }),
  });

  function reset() {
    setUrl("");
    setDescription("");
    setSecret("");
    setSelected([]);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;
    try {
      await create.mutateAsync();
      toast.success("Webhook endpoint created");
      reset();
      onOpenChange(false);
      onCreated();
    } catch {
      toast.error("Failed to create endpoint");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Webhook Endpoint</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ep-url">URL</Label>
            <Input
              id="ep-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-server.com/webhooks"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-desc">Description</Label>
            <Input
              id="ep-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Production notification handler"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-secret">Signing Secret (optional)</Label>
            <Textarea
              id="ep-secret"
              rows={2}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="Leave blank to skip signing"
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              If set, every delivery will include{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                X-Chawowa-Signature: sha256=&lt;hmac&gt;
              </code>
            </p>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Subscribe to Events</p>
            <div className="flex flex-col gap-1.5">
              {eventTypes.map((et) => {
                const on = selected.includes(et.value);
                return (
                  <label
                    key={et.value}
                    className="flex cursor-pointer items-start gap-2"
                  >
                    <Checkbox
                      checked={on}
                      onCheckedChange={(c) =>
                        setSelected((cur) =>
                          c
                            ? [...cur, et.value]
                            : cur.filter((v) => v !== et.value),
                        )
                      }
                      className="mt-0.5"
                    />
                    <span className="text-sm">
                      <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                        {et.value}
                      </code>
                      <span className="ml-1.5 text-muted-foreground">
                        — {et.label}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !url.trim()}>
              {create.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryHistoryDialog({
  endpoint,
  onClose,
}: {
  endpoint: WebhookEndpoint;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const queryKey = ["webhooks", "deliveries", endpoint.id] as const;
  const q = useQuery({
    queryKey,
    queryFn: () => webhooksApi.endpointDeliveries(endpoint.id),
    staleTime: 10_000,
  });

  const retry = useMutation({
    mutationFn: (id: string) => webhooksApi.retryDelivery(id),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const deliveries = q.data ?? [];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Delivery History</DialogTitle>
          <DialogDescription className="truncate">
            {endpoint.url}
          </DialogDescription>
        </DialogHeader>

        {q.isLoading && (
          <div className="space-y-2 py-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {!q.isLoading && deliveries.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No deliveries yet.
          </p>
        )}

        {!q.isLoading && deliveries.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Attempts</th>
                  <th className="px-3 py-2 font-medium">Response</th>
                  <th className="px-3 py-2 font-medium">Sent At</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {deliveries.map((d) => (
                  <DeliveryRow
                    key={d.id}
                    d={d}
                    onRetry={() => retry.mutate(d.id)}
                    retrying={retry.isPending && retry.variables === d.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => qc.invalidateQueries({ queryKey })}
          >
            <RefreshCw className="size-4" />
            Refresh
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryRow({
  d,
  onRetry,
  retrying,
}: {
  d: Delivery;
  onRetry: () => void;
  retrying: boolean;
}) {
  const cls =
    DELIVERY_STATUS_CLS[d.status] ?? "text-muted-foreground bg-muted";
  return (
    <tr>
      <td className="px-3 py-2">
        <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
          {d.event_type}
        </code>
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
            cls,
          )}
        >
          {d.status}
        </span>
      </td>
      <td className="px-3 py-2 tabular-nums">{d.attempt_count}</td>
      <td className="px-3 py-2">
        {d.response_code ? (
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs",
              d.response_code < 300
                ? "border-emerald-200 text-emerald-700 dark:border-emerald-900 dark:text-emerald-400"
                : "border-rose-200 text-rose-700 dark:border-rose-900 dark:text-rose-400",
            )}
          >
            {d.response_code}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {new Date(d.last_attempt_at || d.created_at).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right">
        {(d.status === "failed" || d.status === "dlq") && (
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onRetry}
            disabled={retrying}
            aria-label="Retry delivery"
          >
            <RotateCw
              className={cn("size-4", retrying && "animate-spin")}
            />
          </Button>
        )}
      </td>
    </tr>
  );
}
